import React from "react";
import { Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";

export const CartTable = ({
	t,
	cart,
	getCurrencySymbol,
	onBatchChange,
	onPriceChange,
	onQuantityChange,
	onTaxChange,
	onRemoveItem,
	disableScrollProps,
	updateReturnQuantity,
	itemTaxes,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	totals,
	showReturnTax = true,
}) => {
	const isSameState =
		!patientStateCode ||
		!pharmacyStateCode ||
		pharmacyStateCode === patientStateCode;

	const isGstMode = String(posTaxType || "").toLowerCase() === "gst";
	const taxColCount = showReturnTax ? (isGstMode ? (isSameState ? 3 : 2) : 2) : 0;
	const emptyColSpan = 6 + taxColCount + 2;

	const totalTaxableBeforeDiscount = cart.reduce((sum, item) => {
		const itemKey = `${item._id}-${item.selectedBatchId}`;
		const taxInfo = itemTaxes[itemKey];
		if (!taxInfo) return sum;
		const returnQuantity = item.returnQuantity || 0;
		if (returnQuantity <= 0) return sum;
		const proportion = returnQuantity / (item.saleQuantity || item.quantity || 1);
		return sum + (taxInfo.taxableAmount || 0) * proportion;
	}, 0);

	const billDiscount = totals?.discount || 0;
	const billDiscountRate =
		totalTaxableBeforeDiscount > 0 ? billDiscount / totalTaxableBeforeDiscount : 0;

	return (
		<div className="table-responsive table-wrap">
			<Table className="align-middle rounded shadow-sm table-bordered table-sm" style={{ fontSize: 'smaller' }}>
				<thead>
					<tr>
						<th scope="col" className="text-nowrap">{t("Batch Number")}</th>
						<th scope="col">{t("Product")}</th>
						<th scope="col">{t("Manufacturer")}</th>
						<th scope="col" className="text-center">{t("Price")}</th>
						<th scope="col" className="text-center">{t("Qty")}</th>
						<th scope="col" className="text-center">{t("Return Qty")}</th>
						{showReturnTax && (
							<>
								<th scope="col" className="text-center">{t("Tax %")}</th>
								<th scope="col" className="text-center text-wrap">{t("Total Taxable Value")}</th>
								{isGstMode ? (
									isSameState ? (
										<>
											<th scope="col" className="text-center">{t("CGST")}</th>
											<th scope="col" className="text-center">{t("SGST")}</th>
										</>
									) : (
										<th scope="col" className="text-center">{t("IGST")}</th>
									)
								) : (
									<th scope="col" className="text-center">{t("VAT")}</th>
								)}
							</>
						)}
						<th scope="col" className="text-center">{t("Gross Total")}</th>
						<th scope="col" className="text-center"></th>
					</tr>
				</thead>
				<tbody>
					{cart.map((item) => {
						const itemKey = `${item._id}-${item.selectedBatchId}`;
						const returnQuantity = item.returnQuantity || 0;
						const proportion =
							returnQuantity > 0
								? returnQuantity / (item.saleQuantity || item.quantity || 1)
								: 0;
						const taxInfo = itemTaxes[itemKey] || {};
						const taxRate = taxInfo.taxPercentage || 0;

						const taxableBeforeDiscount = (taxInfo.taxableAmount || 0) * proportion;
						const taxableAfterDiscount =
							taxableBeforeDiscount - taxableBeforeDiscount * billDiscountRate;

						const prorate = 1 - billDiscountRate;
						const rowTax = (taxInfo.tax || 0) * proportion * prorate;
						const rowCGST = (taxInfo.CGST || 0) * proportion * prorate;
						const rowSGST = (taxInfo.SGST || 0) * proportion * prorate;
						const rowIGST = (taxInfo.IGST || 0) * proportion * prorate;
						const rowVAT = (taxInfo.VAT || 0) * proportion * prorate;
						const displayVAT = rowVAT > 0 ? rowVAT : rowTax;

						const grossTotal = showReturnTax
							? taxableAfterDiscount + rowTax
							: (item.salePrice || 0) * returnQuantity;

						return (
							<tr key={itemKey}>
								<td>
									<Form.Select
										size="sm"
										value={item.selectedBatchId || item.stockId || ""}
										onChange={(event) =>
											onBatchChange(item._id, event.target.value)
										}
										disabled
									>
										{item.batchList && item.batchList.length > 0 ? (
											item.batchList.map((batch, index) => (
												<option key={`${batch._id}-${index}`} value={batch._id}>
													{batch.batchNumber}
												</option>
											))
										) : (
											<option value={item.stockId || ""}>
												{item.batchNumber || "N/A"}
											</option>
										)}
									</Form.Select>
								</td>
								<td className="table-product-title" style={{ maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									<OverlayTrigger
										placement="top"
										overlay={
											<Tooltip id={`tooltip-saltName-${item._id}`}>
												<div>
													<strong>{item.brandName}</strong>
													<br />
													<strong>[{item.saltName}]</strong>
												</div>
											</Tooltip>
										}
									>
										<span>{item.brandName || item.saltName}</span>
									</OverlayTrigger>
								</td>
								<td className="table-product-title" style={{ maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									<OverlayTrigger
										placement="top"
										overlay={<Tooltip id={`tooltip-${item._id}`}>{item.manufacturer || "N/A"}</Tooltip>}
									>
										<span>{item.manufacturer}</span>
									</OverlayTrigger>
								</td>
								<td>
									<div className="input-group custom-qty-input table-price-counter-nowrap">
										<button
											type="button"
											className="button-minus icon-shape icon-sm btn-light"
											disabled
										>
											-
										</button>
										<input
											type="number"
											value={(item.salePrice || 0).toFixed(2)}
											className="border-0 quantity-field incrementor"
											disabled
											onChange={(event) =>
												onPriceChange(item._id, item.selectedBatchId, "salePrice", event.target.value)
											}
											{...disableScrollProps}
										/>
										<button
											type="button"
											className="button-plus icon-shape icon-sm btn-light"
											disabled
										>
											+
										</button>
									</div>
								</td>
								<td className="text-center fw-bold">
									{item.quantity || 0}
								</td>
								<td>
									<div className="input-group custom-qty-input table-price-counter-nowrap">
										<button
											type="button"
											className="button-minus icon-shape icon-sm btn-danger"
											onClick={() =>
												updateReturnQuantity(item._id, Math.max(0, (returnQuantity || 0) - 1), item.quantity || 0)
											}
										>
											-
										</button>
										<input
											type="number"
											value={returnQuantity}
											className="border-0 quantity-field incrementor"
											min={0}
											max={item.quantity || 0}
											onChange={(event) =>
												updateReturnQuantity(item._id, Number(event.target.value), item.quantity || 0)
											}
											{...disableScrollProps}
										/>
										<button
											type="button"
											className="button-plus icon-shape icon-sm btn-primary"
											onClick={() =>
												updateReturnQuantity(item._id, Math.min((returnQuantity || 0) + 1, item.quantity || 0), item.quantity || 0)
											}
										>
											+
										</button>
									</div>
								</td>
								{showReturnTax && (
									<>
										<td>
											<input
												type="number"
												value={item.taxPercentage ?? taxRate ?? 0}
												min={0}
												max={100}
												step="0.01"
												className="form-control form-control-sm text-center fw-bold"
												onChange={(event) =>
													onTaxChange(item._id, item.selectedBatchId, event.target.value)
												}
												{...disableScrollProps}
											/>
										</td>
										<td className="text-center fw-bold">
											{getCurrencySymbol()}{taxableAfterDiscount.toFixed(2)}
										</td>
										{isGstMode ? (
											isSameState ? (
												<>
													<td className="text-center text-success">
														{getCurrencySymbol()}{rowCGST.toFixed(2)}
													</td>
													<td className="text-center text-success">
														{getCurrencySymbol()}{rowSGST.toFixed(2)}
													</td>
												</>
											) : (
												<td className="text-center text-success">
													{getCurrencySymbol()}{rowIGST.toFixed(2)}
												</td>
											)
										) : (
											<td className="text-center text-success">
												{getCurrencySymbol()}{displayVAT.toFixed(2)}
											</td>
										)}
									</>
								)}
								<td className="text-center fw-bold" style={{ fontSize: 'small' }}>
									{getCurrencySymbol()}{grossTotal.toFixed(2)}
								</td>
								<td className="text-center">
									<button
										className="btn-danger btn-sm remove-item"
										onClick={() => onRemoveItem(item._id, item.selectedBatchId)}
									>
										&times;
									</button>
								</td>
							</tr>
						);
					})}
					{cart.length === 0 && (
						<tr>
							<td colSpan={emptyColSpan} className="text-center text-muted">
								{t("No items in cart")}
							</td>
						</tr>
					)}
				</tbody>
			</Table>
		</div>
	);
};

export default CartTable;
